import { memo, useCallback } from "react";
import {
    GRID_ACTIONS_COLUMN_TYPE,
    type GridRowIdGetter,
    type GridRowParams
} from "@mui/x-data-grid";
import { FilterVariant } from "consts/filters";
import { TableGridMode } from "consts/table";
import {
    Filters as WaMessagingFilters,
    MenuItem
} from "consts/waMessaging";
import { useTypedSelector } from "hooks";
import { selectConstants } from "features/general/selectors";
import { useWaMessagesSetupsActions } from "features/waMessaging/actions";
import {
    selectIsWaMessagingResourceLoading,
    selectIsWaMessagingResourceUninitialized,
    selectWaMessagingResourceSlice
} from "features/waMessaging/selectors";
import type { WaMessageSetup } from "features/waMessaging/types";
import { convertToOptions } from "util/transformers";
import Resource from "ui/layouts/Resource";
import Table from "ui/widgets/Table";
import WaMessageSetupProvider from "./WaMessageSetupProvider";
import WaMessageSetupDetail from "./WaMessageSetupDetail";
import useRowActions from "./Actions";
import Toolbar from "./Toolbar";
import useRequestQueryOverrideDecorator from "./useRequestQueryOverrideDecorator";

const MessageSetupsResource = () => {
    const { getWaMessagesSetups, downloadCsv } = useWaMessagesSetupsActions();
    const getRowActions = useRowActions();
    const { ConsumerDomain, TransactionStatus, WaPhoneFrequencies } = useTypedSelector(selectConstants);

    const getRowId: GridRowIdGetter<WaMessageSetup> = useCallback(
        ({ coreId }: WaMessageSetup) => coreId as string,
        []
    );

    return (
        <Resource
            breadcrumbsProps={{
                heading: 'Message Setups',
                links: [{ name: '' }],
                sx: { mb: 0 }
            }}
        >
            <Table
                mode={TableGridMode.WaMessagesSetups}
                selectIsTableUninitialized={selectIsWaMessagingResourceUninitialized(MenuItem.WaMessagesSetups)}
                selectIsTableLoading={selectIsWaMessagingResourceLoading(MenuItem.WaMessagesSetups)}
                selectTableDataSlice={selectWaMessagingResourceSlice(MenuItem.WaMessagesSetups)}
                fetchTableData={getWaMessagesSetups}
                downloadCsv={downloadCsv}
                filterTypes={new Map<FilterVariant, string[]>([
                    [FilterVariant.Text, [
                        WaMessagingFilters.merchantId,
                        WaMessagingFilters.merchantName
                    ]],
                    [FilterVariant.SingleOption, [
                        WaMessagingFilters.MOR,
                        WaMessagingFilters.phoneFrequency,
                        WaMessagingFilters.isEnabled
                    ]],
                    [FilterVariant.MultiOption, [WaMessagingFilters.transactionStatuses]]
                ])}
                requestQueryOverrideDecorator={useRequestQueryOverrideDecorator}
                getRowId={getRowId}
                Toolbar={Toolbar}
                columns={[
                    {
                        field: GRID_ACTIONS_COLUMN_TYPE,
                        type: GRID_ACTIONS_COLUMN_TYPE,
                        getActions: (params: GridRowParams) => getRowActions(params as GridRowParams<WaMessageSetup>)
                    }
                ]}
                initialState={{
                    pinnedColumns: {
                        right: [GRID_ACTIONS_COLUMN_TYPE]
                    }
                }}
                LayoutProps={{ sx: { m: '24px 0px' } }}
                FiltersProps={{
                    sx: { m: 0 },
                    filtersModelOverrides: {
                        Domains: convertToOptions(ConsumerDomain ?? []),
                        WaPhoneFrequencies: convertToOptions(WaPhoneFrequencies ?? []),
                        WaTransactionStatuses: convertToOptions(TransactionStatus ?? [])
                    }
                }}
            />
        </Resource>
    );
};

const MessageSetups = () => (
    <WaMessageSetupProvider>
        <MessageSetupsResource />
        <WaMessageSetupDetail />
    </WaMessageSetupProvider>
);

export default memo(MessageSetups);
